import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { selectedProfileIdAtom } from "../store";
import { api } from "@/api/instance";
import { useAuth } from "@/shared/lib/auth-context";
import { mergeProfileMeta } from "../formState";
import { toast } from "@/components/ui/toast";
import { qk } from "@/shared/api/queryKeys";
import {
  getProfileMeta,
  removeProfileMeta,
  setProfileMeta,
  useProfileMetaById,
} from "@/entities/profile";
import { formatProfileTitleTimestamp } from "@/shared/lib/format";
import {
  getTourHoldDisplayTitle,
  isTourProcessingHold,
} from "@/features/demo-tour";
import type { Profile } from "../types";

const SELECTED_PROFILE_KEY = "recsys.selectedProfileId";

function readStoredProfileId(): string {
  try {
    return localStorage.getItem(SELECTED_PROFILE_KEY) ?? "";
  } catch {
    return "";
  }
}

function clearStoredProfileId(): void {
  try {
    localStorage.removeItem(SELECTED_PROFILE_KEY);
  } catch {
    void 0;
  }
}

function pickValidProfileId(candidate: string, list: Profile[]): string {
  if (!candidate) return "";
  if (list.some((p) => p.id === candidate)) return candidate;
  return list.find((p) => p.title === candidate)?.id ?? "";
}

type ProfilesListResult = {
  profiles: Profile[];
};

export function useProfileManager() {
  const auth = useAuth();
  const username = auth.user?.username;
  const queryClient = useQueryClient();

  const [selectedProfileId, setSelectedProfileId] = useAtom(
    selectedProfileIdAtom,
  );
  const profileMetaById = useProfileMetaById();

  const profilesQuery = useQuery({
    queryKey: qk.profiles.list(),
    queryFn: async () => {
      const res = await api.v1.searchProfiles({ offset: 0, limit: 200 });
      const items = res.data?.items ?? [];
      const prevById = new Map(
        (
          queryClient.getQueryData<ProfilesListResult>(qk.profiles.list())
            ?.profiles ?? []
        ).map((p) => [p.id, p.title]),
      );
      for (const it of items) {
        const key = it.client_id ?? it.name;
        const existing = getProfileMeta(queryClient, key) ?? {};
        const merged = mergeProfileMeta(existing, it);
        if (isTourProcessingHold(key)) {
          // Keep processing UI until tour reveal; server may already be completed.
          setProfileMeta(queryClient, key, {
            ...merged,
            status: "processing",
            isTrackingStatus: true,
          });
        } else {
          setProfileMeta(queryClient, key, merged);
        }
      }
      return {
        profiles: items.map((it) => {
          const id = it.client_id ?? it.name;
          if (isTourProcessingHold(id)) {
            return {
              id,
              title:
                prevById.get(id) ??
                getTourHoldDisplayTitle(id) ??
                it.name,
            };
          }
          return { id, title: it.name };
        }),
      } satisfies ProfilesListResult;
    },
    enabled: Boolean(username),
    staleTime: 30_000,
  });

  const profiles = useMemo(
    () => profilesQuery.data?.profiles ?? [],
    [profilesQuery.data?.profiles],
  );
  const isLoadingProfiles = profilesQuery.isPending;
  const profilesError =
    profilesQuery.error &&
    (profilesQuery.error as { status?: number }).status !== 404
      ? String(
          (profilesQuery.error as { message?: string }).message ??
            "Не удалось загрузить профили",
        )
      : null;

  const [hasRestoredSelection, setHasRestoredSelection] = useState(false);
  const profilesData = profilesQuery.data;
  const [appliedProfilesData, setAppliedProfilesData] = useState(profilesData);

  // Restore selection while rendering (not in an effect) so the tour waits
  // until the atom already holds the stored profile.
  if (profilesData !== appliedProfilesData) {
    setAppliedProfilesData(profilesData);
    if (!profilesData) {
      setHasRestoredSelection(false);
    } else {
      setSelectedProfileId((prev) => {
        const candidate = prev || readStoredProfileId();
        const next = pickValidProfileId(candidate, profilesData.profiles);
        if (candidate && !next) clearStoredProfileId();
        return next;
      });
      setHasRestoredSelection(true);
    }
  }

  const selectedMeta = selectedProfileId
    ? profileMetaById[selectedProfileId]
    : undefined;
  const selectedProfileTitle =
    profiles.find((p) => p.id === selectedProfileId)?.title ??
    selectedProfileId;

  const onSelectProfile = useCallback(
    (id: string) => {
      setSelectedProfileId((prev) => {
        if (prev === id) {
          clearStoredProfileId();
          return "";
        }
        return id;
      });
    },
    [setSelectedProfileId],
  );

  useEffect(() => {
    if (!selectedProfileId) return;
    try {
      localStorage.setItem(SELECTED_PROFILE_KEY, selectedProfileId);
    } catch {
      void 0;
    }
  }, [selectedProfileId]);

  const onCreateProfile = useCallback(() => {
    const nextTitle = formatProfileTitleTimestamp(new Date());
    const nextId = nextTitle;
    setSelectedProfileId(nextId);
    setProfileMeta(queryClient, nextId, {
      status: "not_started",
      isInDatabase: false,
      isTrackingStatus: true,
    });
    queryClient.setQueryData<ProfilesListResult>(qk.profiles.list(), (prev) => {
      const base = prev ?? { profiles: [] };
      if (base.profiles.some((p) => p.id === nextId)) return base;
      return {
        profiles: [...base.profiles, { id: nextId, title: nextTitle }],
      };
    });
    return nextId;
  }, [queryClient, setSelectedProfileId]);

  const removeLocalProfile = useCallback(
    (id: string) => {
      queryClient.setQueryData<ProfilesListResult>(qk.profiles.list(), (prev) => {
        if (!prev) return prev;
        return { profiles: prev.profiles.filter((p) => p.id !== id) };
      });
      setSelectedProfileId((prev) => {
        if (prev !== id) return prev;
        clearStoredProfileId();
        return "";
      });
      removeProfileMeta(queryClient, id);
    },
    [queryClient, setSelectedProfileId],
  );

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.v1.deleteProfile(id);
      return id;
    },
    onSuccess: (id) => {
      removeLocalProfile(id);
      void queryClient.invalidateQueries({ queryKey: qk.profiles.all() });
    },
    onError: () => {
      toast.error("Не удалось удалить профиль. Попробуйте позже.");
    },
  });

  const deleteProfile = useCallback(
    async (id: string) => {
      const meta = getProfileMeta(queryClient, id);
      if (!meta?.isInDatabase) {
        removeLocalProfile(id);
        return;
      }
      if (!username) return;
      await deleteMutation.mutateAsync(id);
    },
    [username, deleteMutation, queryClient, removeLocalProfile],
  );

  return {
    profiles,
    selectedProfileId,
    isLoadingProfiles,
    hasRestoredSelection,
    profilesError,
    profileMetaById,
    selectedMeta,
    selectedProfileTitle,
    onSelectProfile,
    onCreateProfile,
    deleteProfile,
  };
}
